import { Alert, Badge, Group, Paper, Stack, Text } from '@mantine/core';
import { IconAlertCircle, IconAlertTriangle, IconUser } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { TravelPaperTitleCheck } from '../model/TravelRequestTypes';

interface TravelPaperTitleAlertProps {
  check: TravelPaperTitleCheck | undefined;
  // Set once a submit was refused for the title, which holds even when the check itself cannot answer.
  refused?: boolean;
}

export function TravelPaperTitleAlert({ check, refused = false }: TravelPaperTitleAlertProps) {
  const { t } = useTranslation();
  const found = check?.matches ?? [];

  if (found.length === 0 && !refused) {
    return null;
  }

  // An exact match registered after a resubmitted request does not take its title, so the server's verdict decides.
  const taken = refused || check?.taken === true;
  // One person with two requests on the same title would otherwise be listed twice.
  const matches = found.filter(
    (match, index, all) =>
      all.findIndex(other => other.requester.id === match.requester.id && other.paperTitle === match.paperTitle) ===
      index,
  );
  const similarityLabel = (similarity: number) =>
    t('travel.requests.paperTitleCheck.similarity', { percentage: Math.min(99, Math.round(similarity * 100)) });

  return (
    <Alert
      color={taken ? 'red' : 'yellow'}
      icon={taken ? <IconAlertCircle size={18} /> : <IconAlertTriangle size={18} />}
      title={t(`travel.requests.paperTitleCheck.${taken ? 'takenTitle' : 'similarTitle'}`)}
    >
      <Stack gap="xs">
        <Text size="sm">{t(`travel.requests.paperTitleCheck.${taken ? 'takenText' : 'similarText'}`)}</Text>
        {matches.length > 0 && (
          <Stack
            role="list"
            aria-label={t('travel.requests.paperTitleCheck.matches')}
            gap={6}
          >
            {matches.map(match => (
              <Paper
                key={`${match.requester.id}-${match.paperTitle}`}
                role="listitem"
                withBorder
                radius="sm"
                px="sm"
                py={6}
              >
                <Group
                  justify="space-between"
                  align="flex-start"
                  gap="xs"
                >
                  {/* The basis sends the badge to its own line on a narrow screen instead of squeezing the title. */}
                  <Stack
                    gap={2}
                    style={{ flex: '1 1 180px', minWidth: 0 }}
                  >
                    <Text
                      size="sm"
                      fw={600}
                    >
                      {match.paperTitle}
                    </Text>
                    <Group
                      gap={4}
                      wrap="nowrap"
                      align="flex-start"
                    >
                      <IconUser
                        size={14}
                        color="var(--mantine-color-dimmed)"
                        style={{ flexShrink: 0, marginTop: 2 }}
                      />
                      <Text
                        size="xs"
                        c="dimmed"
                      >
                        {t('travel.requests.paperTitleCheck.usedBy')}{' '}
                        <Text
                          span
                          fw={500}
                          c="var(--mantine-color-text)"
                        >
                          {match.requester.name}
                        </Text>
                      </Text>
                    </Group>
                  </Stack>
                  <Badge
                    color={match.exact ? 'red' : 'yellow'}
                    variant="light"
                    style={{ flexShrink: 0 }}
                  >
                    {match.exact ? t('travel.overlaps.exact') : similarityLabel(match.similarity)}
                  </Badge>
                </Group>
              </Paper>
            ))}
          </Stack>
        )}
      </Stack>
    </Alert>
  );
}
